import { useState, useRef, useEffect } from "react";
import type { Lead, Priority } from "@/mocks/leadsData";
import { sourceBadgeColors, stageBadgeColors, priorityDotColors, getScoreBadgeColors } from "@/mocks/leadsData";

type Props = {
  leads: Lead[];
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
  allSelected: boolean;
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
  onDelete: (lead: Lead) => void;
};

export default function LeadsListView({
  leads, selectedIds, onToggleSelect, onToggleSelectAll, allSelected,
  onView, onEdit, onDelete,
}: Props) {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getInitials = (name?: string) =>
    name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "U";

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" ref={menuRef}>
      {/* Column labels */}
      <div className="flex items-center px-4 py-2.5 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="w-8 flex-shrink-0">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleSelectAll}
            className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
        </div>
        <div className="w-7 flex-shrink-0" />
        <div className="flex-1 min-w-0 pr-4">Lead</div>
        <div className="w-[100px] flex-shrink-0 hidden sm:block">Source</div>
        <div className="w-[100px] flex-shrink-0 hidden sm:block">Stage</div>
        <div className="w-[110px] flex-shrink-0 hidden md:block">Phone</div>
        <div className="w-[60px] flex-shrink-0 hidden lg:block text-center">Score</div>
        <div className="w-[110px] flex-shrink-0 hidden lg:block">Assigned To</div>
        <div className="w-[72px] flex-shrink-0 text-right">Actions</div>
      </div>

      {/* Rows */}
      {leads.map((lead) => (
        <div
          key={lead.id}
          className="flex items-center px-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
          style={{ height: "44px" }}
        >
          {/* Checkbox */}
          <div className="w-8 flex-shrink-0">
            <input
              type="checkbox"
              checked={selectedIds.has(lead.id)}
              onChange={() => onToggleSelect(lead.id)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
            />
          </div>

          {/* Mini avatar */}
          <div className="w-7 flex-shrink-0">
            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700">
              {getInitials(lead.name)}
            </div>
          </div>

          {/* Name + Company */}
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">{lead.name}</span>
              <span className="text-xs text-gray-400 truncate hidden sm:inline">{lead.company}</span>
            </div>
          </div>

          {/* Source badge */}
          <div className="w-[100px] flex-shrink-0 hidden sm:block">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${sourceBadgeColors[lead.source] || "bg-indigo-50 text-indigo-700"}`}>
              {lead.sourceAccount ? `${lead.source} \u2022 ${lead.sourceAccount}` : lead.source}
            </span>
          </div>

          {/* Stage badge */}
          <div className="w-[100px] flex-shrink-0 hidden sm:block">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stageBadgeColors[lead.stage] || "bg-gray-100 text-gray-700"}`}>
              {lead.stage}
            </span>
          </div>

          {/* Phone */}
          <div className="w-[110px] flex-shrink-0 hidden md:block">
            <span className="text-xs text-gray-500">{lead.phone}</span>
          </div>

          {/* Score */}
          <div className="w-[60px] flex-shrink-0 hidden lg:flex justify-center">
            <span className={`inline-flex items-center justify-center w-8 h-7 rounded-full text-[10px] font-bold ${getScoreBadgeColors(lead.score || 50)}`}>
              {lead.score ?? 50}
            </span>
          </div>

          {/* Assigned */}
          <div className="w-[110px] flex-shrink-0 hidden lg:block">
            <span className="text-xs text-gray-600">{lead.assigned || "Unassigned"}</span>
          </div>

          {/* Actions */}
          <div className="w-[72px] flex-shrink-0 flex items-center justify-end gap-0.5">
            <button
              onClick={() => onView(lead)}
              className="p-1 rounded hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
              title="View"
            >
              <i className="ri-eye-line text-[13px]" />
            </button>
            <button
              onClick={() => onEdit(lead)}
              className="p-1 rounded hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
              title="Edit"
            >
              <i className="ri-edit-line text-[13px]" />
            </button>
            <div className="relative">
              <button
                onClick={() => setOpenMenuId(openMenuId === lead.id ? null : lead.id)}
                className="p-1 rounded hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                title="More"
              >
                <i className="ri-more-2-fill text-[13px]" />
              </button>
              {openMenuId === lead.id && (
                <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-20">
                  <button
                    onClick={() => { onEdit(lead); setOpenMenuId(null); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-edit-line text-gray-400" /> Edit
                  </button>
                  <hr className="my-0.5 border-gray-100" />
                  <button
                    onClick={() => { onDelete(lead); setOpenMenuId(null); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line" /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {leads.length === 0 && (
        <div className="py-16 text-center">
          <i className="ri-user-search-line text-3xl text-gray-300 mb-2 block" />
          <p className="text-sm text-gray-500">No leads found</p>
        </div>
      )}
    </div>
  );
}